// services/kontoAnalytics.js — аналитичностите и номенклатурите на фирмите
// от Конто 6 в базата на CRM-а (ПРОМТ-31, решенията на Макс от 28.10.2026).
//
// Защо: статиите на 60x (ном. 165/166/167…) са РАЗЛИЧНИ във всяка фирма
// (ОТГОВОР-ПРОМТ-30: „01" по 602 е „наем" в 11 фирми, „услуги" в 6, „телефон"
// в 5), а Конто при внос на записвания създава липсващ елемент ТОЧНО с кода
// от партидата и името от колона L_<ном.>_NAME, без да преименува
// съществуващ код (името от файла се пренебрегва тихо). Затова CRM-ът трябва
// да знае за всяка фирма: как е разбита всяка сметка (нивата), кои елементи
// има всяка номенклатура, и да сверява ПО ИМЕ, не по код.
//
// Данните идват от три таблици на агента на Конто (01-Текущо\конто\):
//  - „сметки-аналитичности-*.xlsx", лист „Сметки": Фирма, ЕИК, Сметка, Име,
//    Ниво №, Вид на нивото, Номенклатура №, Име на номенклатурата, Дължина…
//    (старият файл — 40x/41x/45x/50x/70x; новият — гр. 20/21/30/60/61/65);
//  - „номенклатури-разходи-всички-фирми.xlsx": „Елементи" (ЕИК, Номенклатура
//    №, Код, Наименование, Дължина, Брой статии 2026, Последно движение) и
//    „Сметки→Статии" (кой код по коя сметка колко пъти е ползван през 2026).
//
// Къде стоят: konto_account_levels — един ред на фирма
//   { company_id, eik, accounts: { '601': { name, levels: [{ no, kind, nom,
//     nom_name, len, role }], roles: ['activity','item'] | null, text,
//     used: { '2': { '02': 15 } } } }, imported_at };
// konto_nsb — един ред на фирма и номенклатура
//   { company_id, nom, nom_name, len, items: [{ code, name, count, last,
//     source: 'konto' | 'crm', crm_name?, category?, article_id? }] }.
// source 'crm' = елемент, който CRM-ът е дал в TXT (Конто го създава при
// вноса); при следващия внос от Конто той става 'konto', а crm_name пази
// какво име му е дал CRM-ът — ако Конто го показва с друго име, експортът
// спира (т. 5.3 от ОТГОВОР-30: кодът тихо би отишъл по чуждо значение).
//
// Чисти функции + малко четене/писане през db (само през фасадата).

const kontoConfig = require('./kontoConfig');

const s = (v) => (v == null ? '' : String(v)).replace(/\s+/g, ' ').trim();
const digits = (v) => String(v == null ? '' : v).replace(/\D/g, '');
// ЕИК на фирма за сравнение: Конто пише „040058584", CRM-ът понякога
// „40058584" — без водещите нули (както kontoSync.firmKey).
const firmKey = (v) => digits(v).replace(/^0+/, '');

// Мерките (ном. $3) — фиксирана таблица (решение 5 на Макс): един и същ
// списък има в 96–100 фирми; има ДВЕ мерки за кг (005 и 165) и две за брой
// (001 и 796) — взимат се най-ползваните. Ключът е measureKey() на мярката.
const MEASURES = [
  ['бр', '796', 'бр.'], ['кг', '165', 'кг'], ['л', '112', 'л'], ['м', '006', 'м'],
  ['м2', '055', 'м²'], ['м3', '167', 'м³'], ['тон', '166', 'тон'], ['час', '004', 'час'],
  ['пакет', '002', 'пакет'], ['кашон', '003', 'кашон'],
];
const MEASURE_ALIAS = { 'брой': 'бр', 'броя': 'бр', 'бройка': 'бр', 'pcs': 'бр', 'kg': 'кг', 'l': 'л', 'т': 'тон', 't': 'тон', 'm': 'м', 'm2': 'м2', 'm3': 'м3', 'ч': 'час', 'h': 'час', 'пак': 'пакет', 'каш': 'кашон' };
function measureKey(unit) {
  const k = String(unit == null ? '' : unit).toLowerCase().replace(/\s+/g, '').replace(/\.+$/, '').replace(/²/g, '2').replace(/³/g, '3').replace(/кв\.?м|м\.?кв/, 'м2').replace(/куб\.?м|м\.?куб/, 'м3');
  return MEASURE_ALIAS[k] || k;
}

// ---- Ролите на нивата ---------------------------------------------------------
// Какво попълва CRM-ът в нивото: partner (80/85 — шифър/ЕИК), no (№ на
// документа), date, deal (51/510 — вид сделка), activity (ном. 20 — кодът от
// настройката на фирмата), item (статия 165–175… — картата на категориите),
// article (14/15/50 — кодът за Конто на артикула), measure ($3), project
// (ном. 208 „Проект" — ФОНДАЦИЯ ГИП) и mol (ном. 201 „МОЛ/Склад" по 302/304 —
// ПРЕМИЕР-АК, ФИНТ КОМЕРС): кодът от настройката на фирмата, като вида
// дейност (ПРОМТ-45). null =
// ниво, което CRM-ът не умее (валута, проект, свободен текст…) — такава
// сметка се записва в Конто на ръка (levelsError).
const ARTICLE_NOMS = new Set(['14', '15', '50', '140', '171', '175', '505', '855']);
// Партньорски номенклатури, които в листа понякога са „номенклатура", не
// „контрагент (номенклатура)" (44 „Доставчици по аванси", 80 при СД ХЕВИС).
const PARTNER_NOMS = new Set(['80', '85', '800', '850', '44']);
function roleOf(level) {
  const kind = s(level.kind).toLowerCase();
  const nom = s(level.nom);
  const name = s(level.nom_name).toLowerCase();
  if (kind.startsWith('контрагент')) return 'partner';
  if (kind === 'номенклатура' && (PARTNER_NOMS.has(nom) || /доставчи|клиент|контрагент/.test(name))) return 'partner';
  if (kind.startsWith('документ')) return 'no';
  if (kind === 'дата') return 'date';
  if (kind === 'вид сделка' || nom === '51' || nom === '510') return 'deal';
  if (kind === 'мярка' || nom === '$3') return 'measure';
  if (kind === 'номенклатура') {
    if (nom === '20') return 'activity';
    if (nom === '208' || /проект/.test(name)) return 'project';
    if (nom === '201' || /(^|[^а-я])мол([^а-я]|$)|склад/.test(name)) return 'mol';
    if (/вид на (материала|стоката|продукцията)/.test(name) || ARTICLE_NOMS.has(nom)) return 'article';
    if (/стати|вид(ове)? ?(на )?разход/.test(name)) return 'item';
    const n = Number(nom);
    if (Number.isInteger(n) && n >= 160 && n <= 179) return 'item';
    if (nom === '670') return 'item';
  }
  return null;
}

// ---- Четене на таблиците -------------------------------------------------------
// aoa → редове по заглавие; заглавният ред се търси (може да не е първият).
function rowsOf(aoa, required) {
  const rows = Array.isArray(aoa) ? aoa : [];
  const hi = rows.findIndex((r) => Array.isArray(r) && required.every((c) => r.some((x) => s(x) === c)));
  if (hi < 0) return null;
  const head = rows[hi].map(s);
  const idx = (c) => head.indexOf(c);
  return rows.slice(hi + 1).filter((r) => Array.isArray(r) && r.some((x) => s(x))).map((r) => {
    const o = {};
    for (const c of head) if (c) o[c] = r[idx(c)];
    return o;
  });
}

// Дата от клетка: число (Excel), „2026-09-30…" или „30.09.2026" → ISO.
function isoDate(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number' && isFinite(v) && v > 0) return new Date(Math.round((v - 25569) * 86400000)).toISOString().slice(0, 10);
  const t = s(v);
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) return m[1] + '-' + m[2] + '-' + m[3];
  m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(t);
  if (m) return m[3] + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0');
  return '';
}

// Работна книга (SheetJS) → { accounts, elements, usage, sheets }. Коя
// таблица е дошла се познава по листовете и заглавията, не по името на файла.
// Хвърля, ако нито един лист не е познат.
function readWorkbook(X, wb) {
  const out = { accounts: [], elements: [], usage: [], sheets: [] };
  const aoa = (name) => X.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', raw: true });
  for (const name of wb.SheetNames) {
    const a = aoa(name);
    let rows = rowsOf(a, ['ЕИК на фирмата', 'Сметка', 'Вид на нивото']);
    if (rows) {
      out.sheets.push(name);
      for (const r of rows) {
        out.accounts.push({
          firm: s(r['Фирма']), eik: s(r['ЕИК на фирмата']), account: s(r['Сметка']), account_name: s(r['Име на сметката']),
          no: Number(r['Ниво №']) || 0, kind: s(r['Вид на нивото']), nom: s(r['Номенклатура №']), nom_name: s(r['Име на номенклатурата']),
          len: Number(r['Дължина']) || 0, example: s(r['Пример (цялата партида)']),
        });
      }
      continue;
    }
    rows = rowsOf(a, ['ЕИК на фирмата', 'Номенклатура №', 'Код', 'Наименование', 'Дължина на кода']);
    if (rows) {
      out.sheets.push(name);
      for (const r of rows) {
        const code = s(r['Код']);
        if (!code) continue;
        out.elements.push({
          firm: s(r['Фирма']), eik: s(r['ЕИК на фирмата']), nom: s(r['Номенклатура №']), nom_name: s(r['Име на номенклатурата']),
          code, name: s(r['Наименование']), len: Number(r['Дължина на кода']) || 0,
          count: Number(r['Брой статии 2026']) || 0, last: isoDate(r['Последно движение']),
          active: r['Активен'] === '' || r['Активен'] == null ? true : !!Number(r['Активен']),
        });
      }
      continue;
    }
    rows = rowsOf(a, ['ЕИК на фирмата', 'Сметка', 'Ниво №', 'Код', 'Брой статии 2026']);
    if (rows) {
      out.sheets.push(name);
      for (const r of rows) {
        out.usage.push({ eik: s(r['ЕИК на фирмата']), account: s(r['Сметка']), no: Number(r['Ниво №']) || 0, nom: s(r['Номенклатура №']), code: s(r['Код']), count: Number(r['Брой статии 2026']) || 0 });
      }
    }
  }
  if (!out.sheets.length) throw new Error('Това не е таблица от Конто — няма лист „Сметки" (с „Вид на нивото"), „Елементи" или „Сметки→Статии".');
  return out;
}

// ---- Нивата на сметките → записът на фирмата ------------------------------------
// Текстът на сметката за екрана („80 Доставчик / № / дата") — както в
// config/konto.analytics.json.
function levelText(l) {
  if (l.role === 'deal') return 'вид сделка (' + l.nom + ')';
  if (l.role === 'no') return '№';
  if (l.role === 'date') return 'дата';
  return (l.nom ? l.nom + ' ' : '') + l.nom_name;
}
// account: по сметки извън гр. 20/21/30/60/61/65 (401, 412, 4532, 70x…)
// CRM-ът попълва само контрагент / № / дата / вид сделка — ниво „вид
// дейност" или „артикул" там е ниво, което не умее (null, както в
// konto.analytics.json за ЕСПРИ, РОТЕКСТ и САТЕЛИТ БИЛД).
const DOC_ROLES = new Set(['partner', 'no', 'date', 'deal']);
function accountEntry(name, levels, account) {
  const docOnly = !/^(2|3|6)/.test(String(account || ''));
  const lv = levels.slice().sort((a, b) => a.no - b.no).map((l) => ({ no: l.no, kind: l.kind, nom: l.nom, nom_name: l.nom_name, len: l.len, role: roleOf(l) }));
  if (docOnly) for (const l of lv) if (l.role && !DOC_ROLES.has(l.role)) l.role = null;
  const roles = lv.map((l) => l.role);
  return {
    name, levels: lv,
    roles: roles.some((r) => r === null) ? null : roles,
    text: lv.length ? lv.map(levelText).join(' / ') : 'без аналитичност',
  };
}
// Редовете на „Сметки" → Map firmKey → { firm, eik, accounts }.
function accountsByFirm(rows) {
  const firms = new Map();
  const tmp = new Map(); // key → acc → { name, levels[] }
  for (const r of rows) {
    const key = firmKey(r.eik);
    if (!key || !r.account) continue;
    if (!firms.has(key)) { firms.set(key, { firm: r.firm, eik: r.eik, accounts: {} }); tmp.set(key, new Map()); }
    const accs = tmp.get(key);
    if (!accs.has(r.account)) accs.set(r.account, { name: r.account_name, levels: [] });
    const a = accs.get(r.account);
    if (r.kind && r.kind !== 'без аналитичност') a.levels.push(r);
  }
  for (const [key, accs] of tmp) {
    const f = firms.get(key);
    for (const [acc, v] of accs) f.accounts[acc] = accountEntry(v.name, v.levels, acc);
  }
  return firms;
}

// ---- Планът на вноса --------------------------------------------------------------
// parsed: readWorkbook(); companies: db.all('companies'); levelsRows /
// nsbRows: заварените редове; opts (по избор): { articles, groups,
// stockAccountOf(article, group, company) } — за „Код за Конто" на
// артикулите по име (решение 6). Нищо не пише — apply() записва плана.
// opts.snapshot (ПРОМТ-45, вносът от JSON): файлът е ЦЯЛАТА номенклатура на
// фирмата — изчезналият от Конто елемент остава с inactive (не се трие:
// стари доставки и картата го сочат), а планът пази разликите (нови /
// изчезнали / преименувани) за екрана. opts.extra: Map ключ на фирмата →
// допълнителни полета на реда в konto_account_levels (вид сделка, видове
// документи, предупреждения).
function plan(parsed, companies, levelsRows, nsbRows, opts) {
  const snapshot = !!(opts && opts.snapshot);
  const byKey = new Map();
  for (const c of companies || []) { const k = firmKey(c.eik); if (k && !byKey.has(k)) byKey.set(k, c); }
  const levelsByCo = new Map((levelsRows || []).map((r) => [Number(r.company_id), r]));
  const nsbByCo = new Map();
  for (const r of nsbRows || []) {
    const k = Number(r.company_id);
    if (!nsbByCo.has(k)) nsbByCo.set(k, new Map());
    nsbByCo.get(k).set(String(r.nom), r);
  }
  const out = { firms: new Map(), missing: new Map(), sheets: parsed.sheets || [] };
  const firmOf = (key, name, eik) => {
    const c = byKey.get(key);
    if (!c) { if (!out.missing.has(key)) out.missing.set(key, { name, eik }); return null; }
    if (!out.firms.has(key)) out.firms.set(key, { company_id: c.id, company: c, name: c.name, eik: c.eik || eik, levels: null, nsb: [], articles: [], ambiguous: [], mismatch: [], diff: { added: [], gone: [], renamed: [] }, counts: { accounts: 0, elements: 0, noms: 0, articles: 0 } });
    return out.firms.get(key);
  };
  // Нивата: записът на фирмата = заварените сметки + тези от файла (файлът
  // презаписва същата сметка; сметка, която я няма във файла, остава).
  for (const [key, f] of accountsByFirm(parsed.accounts || [])) {
    const firm = firmOf(key, f.firm, f.eik);
    if (!firm) continue;
    const ex = levelsByCo.get(firm.company_id);
    firm.levels = { accounts: Object.assign({}, (ex && ex.accounts) || {}, f.accounts) };
    firm.counts.accounts = Object.keys(f.accounts).length;
  }
  // Ползването през 2026 („Сметки→Статии") → accounts[acc].used[ниво][код].
  for (const u of parsed.usage || []) {
    const key = firmKey(u.eik);
    const firm = byKey.has(key) ? firmOf(key, '', u.eik) : null;
    if (!firm) continue;
    if (!firm.levels) { const ex = levelsByCo.get(firm.company_id); firm.levels = { accounts: Object.assign({}, (ex && ex.accounts) || {}) }; }
    const a = firm.levels.accounts[u.account];
    if (!a) continue;
    a.used = a.used || {};
    a.used[String(u.no)] = a.used[String(u.no)] || {};
    a.used[String(u.no)][u.code] = u.count;
  }
  // Елементите: по фирма и номенклатура; CRM-ските, които Конто още не
  // показва, остават (с source 'crm'); показан от Конто код взима името на
  // Конто и пази crm_name за проверката при експорта.
  const grouped = new Map(); // key → nom → { nom_name, len, items: Map code → item }
  for (const e of parsed.elements || []) {
    const key = firmKey(e.eik);
    if (!key) continue;
    if (!grouped.has(key)) grouped.set(key, { firm: e.firm, eik: e.eik, noms: new Map() });
    const g = grouped.get(key);
    if (!g.noms.has(e.nom)) g.noms.set(e.nom, { nom_name: e.nom_name, len: e.len, items: new Map() });
    const n = g.noms.get(e.nom);
    if (!n.items.has(e.code)) n.items.set(e.code, Object.assign({ code: e.code, name: e.name, count: e.count, last: e.last, source: 'konto' }, e.active === false ? { active: false } : {}));
  }
  for (const [key, g] of grouped) {
    const firm = firmOf(key, g.firm, g.eik);
    if (!firm) continue;
    const exNoms = nsbByCo.get(firm.company_id) || new Map();
    for (const [nom, n] of g.noms) {
      const ex = exNoms.get(nom);
      const items = [...n.items.values()];
      const seen = new Set(items.map((i) => i.code));
      const oldItems = (ex && Array.isArray(ex.items)) ? ex.items : [];
      const diffing = snapshot && ex && ex.imported_at;
      for (const old of oldItems) {
        const now = n.items.get(old.code);
        if (old.source !== 'crm') {
          if (diffing && !old.inactive) {
            if (now && normName(old.name) !== normName(now.name)) firm.diff.renamed.push({ nom, code: old.code, from: old.name, to: now.name });
            if (!now) firm.diff.gone.push({ nom, code: old.code, name: old.name });
          }
          // Изчезнал от Конто: при snapshot се пази като неактивен.
          if (!now && snapshot && !seen.has(old.code)) { items.push(Object.assign({}, old, { inactive: true, active: false })); seen.add(old.code); }
          continue;
        }
        if (now) Object.assign(now, { crm_name: old.crm_name || old.name, category: old.category, article_id: old.article_id });
        else if (!seen.has(old.code)) { items.push(old); seen.add(old.code); }
      }
      if (diffing) {
        // „Нов" е само код, който Конто не е показвал и CRM-ът не е давал.
        const before = new Set(oldItems.filter((i) => !i.inactive || i.source === 'crm').map((i) => i.code));
        for (const it of n.items.values()) if (!before.has(it.code)) firm.diff.added.push({ nom, code: it.code, name: it.name });
      }
      firm.nsb.push({ id: ex ? ex.id : null, nom, nom_name: n.nom_name, len: n.len, items });
      firm.counts.noms++;
      firm.counts.elements += n.items.size;
    }
  }
  if (opts && Array.isArray(opts.articles) && typeof opts.stockAccountOf === 'function') {
    for (const firm of out.firms.values()) planArticles(firm, levelsByCo.get(firm.company_id), nsbByCo.get(firm.company_id), opts);
  }
  return out;
}

// „Код за Конто" на артикулите по ИМЕ (решение 6, както шифрите при
// kontoSync): артикул без код получава кода на елемента със същото
// (нормализирано) име в номенклатурата на складовата му сметка (304 → 50,
// 302 → 14, 303 → 15 — по нивата на фирмата). Попълненото не се сменя.
// Двойните (няколко елемента с това име или няколко артикула с едно име) и
// несъвпадащите (заварен код с друго име в Конто) — само в отчета, за решение.
function planArticles(firm, exLevels, exNsb, opts) {
  const accounts = (firm.levels && firm.levels.accounts) || (exLevels && exLevels.accounts) || {};
  const noms = new Map();
  for (const n of firm.nsb) noms.set(String(n.nom), n);
  for (const [nom, r] of (exNsb || new Map())) if (!noms.has(nom)) noms.set(nom, { nom, len: r.len, items: Array.isArray(r.items) ? r.items : [] });
  const groups = new Map((opts.groups || []).filter((g) => Number(g.company_id) === Number(firm.company_id)).map((g) => [Number(g.id), g]));
  const arts = opts.articles.filter((a) => Number(a.company_id) === Number(firm.company_id) && a.active !== false);
  const byNomName = new Map(); // ном|име → [артикули без код]
  for (const a of arts) {
    const acc = opts.stockAccountOf(a, a.group_id != null ? groups.get(Number(a.group_id)) : null, firm.company);
    const entry = acc && accounts[String(acc)];
    const lv = entry && Array.isArray(entry.levels) ? entry.levels.find((l) => l.role === 'article') : null;
    if (!lv) continue;
    const n = noms.get(String(lv.nom));
    if (!n) continue;
    const own = s(a.accounting && a.accounting.konto6 && a.accounting.konto6.code);
    if (own) {
      const ex = n.items.find((i) => i.code === own);
      if (ex && normName(ex.name) !== normName(a.name)) firm.mismatch.push({ id: a.id, name: a.name, code: own, konto_name: ex.name, nom: String(lv.nom) });
      continue;
    }
    const key = lv.nom + '|' + normName(a.name);
    if (!byNomName.has(key)) byNomName.set(key, { nom: String(lv.nom), len: lv.len, list: [] });
    byNomName.get(key).list.push(a);
  }
  for (const [key, g] of byNomName) {
    const nn = key.slice(key.indexOf('|') + 1);
    const n = noms.get(g.nom);
    const hits = n.items.filter((i) => normName(i.name) === nn && (!g.len || String(i.code).length <= g.len));
    if (!hits.length) continue;
    if (hits.length > 1 || g.list.length > 1) {
      for (const a of g.list) firm.ambiguous.push({ id: a.id, name: a.name, nom: g.nom, codes: hits.map((h) => h.code + ' ' + h.name), articles: g.list.length });
      continue;
    }
    firm.articles.push({ id: g.list[0].id, name: g.list[0].name, code: hits[0].code, nom: g.nom });
  }
  firm.counts.articles = firm.articles.length;
}

function summary(p, done) {
  const firms = [...p.firms.values()].map((f) => ({
    company_id: f.company_id, name: f.name, eik: f.eik, accounts: f.counts.accounts, noms: f.counts.noms, elements: f.counts.elements,
    articles: f.counts.articles || 0, ambiguous: f.ambiguous || [], mismatch: f.mismatch || [],
    diff: f.diff || { added: [], gone: [], renamed: [] },
  }));
  const totals = firms.reduce((t, f) => ({ firms: t.firms + 1, accounts: t.accounts + f.accounts, noms: t.noms + f.noms, elements: t.elements + f.elements, articles: t.articles + f.articles, ambiguous: t.ambiguous + f.ambiguous.length, mismatch: t.mismatch + f.mismatch.length }), { firms: 0, accounts: 0, noms: 0, elements: 0, articles: 0, ambiguous: 0, mismatch: 0 });
  return { done: !!done, sheets: p.sheets, totals, firms, missing: [...p.missing.values()] };
}

// Записва плана в една транзакция.
function apply(db, p, who) {
  const now = new Date().toISOString();
  db.transaction(() => {
    for (const f of p.firms.values()) {
      if (f.levels) {
        const ex = db.all('konto_account_levels').find((r) => Number(r.company_id) === Number(f.company_id));
        const data = Object.assign({ company_id: f.company_id, eik: f.eik, accounts: f.levels.accounts, imported_at: now, imported_by_name: who || '' }, f.extra || {});
        if (ex) db.update('konto_account_levels', ex.id, data); else db.insert('konto_account_levels', data);
      }
      for (const n of f.nsb) {
        const data = { company_id: f.company_id, nom: n.nom, nom_name: n.nom_name, len: n.len, items: n.items, imported_at: now };
        if (n.id) db.update('konto_nsb', n.id, data); else db.insert('konto_nsb', data);
      }
      for (const x of f.articles || []) {
        const art = db.get('articles', x.id);
        if (!art || Number(art.company_id) !== Number(f.company_id)) continue;
        const k6 = (art.accounting && art.accounting.konto6) || {};
        if (s(k6.code)) continue; // попълнен междувременно — не се сменя
        db.update('articles', art.id, { accounting: Object.assign({}, art.accounting || {}, { konto6: Object.assign({}, k6, { code: x.code }) }) });
      }
    }
  });
  return summary(p, true);
}

// ---- Четене за фирма --------------------------------------------------------------
// Копие на фирмата с konto_analytics (kontoConfig.analyticsOf го чете преди
// JSON файла). Без ред в базата — както досега (konto.analytics.json).
function levelsRowOf(db, companyId) {
  return db.all('konto_account_levels').find((r) => Number(r.company_id) === Number(companyId)) || null;
}
function withAnalytics(db, company) {
  if (!company) return company;
  const row = levelsRowOf(db, company.id);
  return row ? Object.assign({}, company, { konto_analytics: { accounts: row.accounts, imported_at: row.imported_at } }) : company;
}
// Номенклатурите на фирмата: Map ном → { nom_name, len, items }.
function nsbOf(db, companyId, noms) {
  const want = noms ? new Set(noms.map(String)) : null;
  const out = new Map();
  for (const r of db.all('konto_nsb')) {
    if (Number(r.company_id) !== Number(companyId)) continue;
    if (want && !want.has(String(r.nom))) continue;
    out.set(String(r.nom), { id: r.id, nom: String(r.nom), nom_name: r.nom_name, len: r.len, items: Array.isArray(r.items) ? r.items : [] });
  }
  return out;
}

// ---- Помощни за експорта и екрана --------------------------------------------------
// Следващият свободен код в номенклатурата: най-малкото число, чийто код с
// дължина len го няма сред заетите (Конто НЕ ползва „max + 1" и не допълва с
// нули — кодът се пише точно с дължината). skip: числа, които не се дават
// (99 = „приключване" при статиите).
function freeCode(used, len, opts) {
  const L = Math.max(1, Number(len) || 2);
  const taken = new Set([...(used || [])].map(String));
  const skip = new Set(((opts && opts.skip) || []).map(Number));
  const max = Math.pow(10, L) - 1;
  for (let n = 1; n <= max; n++) {
    if (skip.has(n)) continue;
    const code = String(n).padStart(L, '0');
    if (!taken.has(code)) return code;
  }
  return null;
}

// Име за сравнение: малки букви, без пунктуация и двойни интервали, без
// „разходи за" отпред („Разходи за наем" = „наем").
function normName(v) {
  return String(v || '').toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim()
    .replace(/^(разходи за|разход за|р ди за|р ди|разх за)\s+/, '').trim();
}
// Предложение за статия по ИМЕ (решение 2): точно съвпадение → нормализирано
// → синоним (ПРОМТ-45: точните имена от 100-те фирми, по реда си) → ключова
// дума (при няколко — най-ползваната през 2026). null = няма.
// Ключовата дума е само за статия, която фирмата ПОЛЗВА (count > 0) и която
// не е взета от друга категория (claimed): „наем автомобил" без нито едно
// записване не е „Наем" (ТЕЛ ДЕМ), а „03 услуга" е само „Други външни услуги".
// Неактивен елемент (изчезнал от Конто или ACTIVE = 0) не се предлага.
function suggest(category, items, claimed) {
  const list = (items || []).filter((i) => i && i.code && !i.inactive && i.active !== false);
  const name = s(category.name);
  let hit = list.find((i) => s(i.name).toLowerCase() === name.toLowerCase());
  if (hit) return { code: hit.code, name: hit.name, how: 'exact' };
  const nn = normName(name);
  hit = list.find((i) => normName(i.name) === nn);
  if (hit) return { code: hit.code, name: hit.name, how: 'norm' };
  const byUse = (a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || String(a.code).localeCompare(String(b.code));
  for (const syn of (category.synonyms || []).map(normName).filter(Boolean)) {
    const same = list.filter((i) => normName(i.name) === syn).sort(byUse);
    if (same.length) return { code: same[0].code, name: same[0].name, how: 'synonym' };
  }
  const kws = (category.keywords || []).map((k) => normName(k)).filter(Boolean);
  const cand = list.filter((i) => { const n = normName(i.name); return Number(i.count) > 0 && !(claimed && claimed.has(i.code)) && kws.some((k) => n.includes(k)); });
  if (cand.length) {
    cand.sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || String(a.code).localeCompare(String(b.code)));
    return { code: cand[0].code, name: cand[0].name, how: 'keyword' };
  }
  return null;
}
// Кодът на вида дейност (ном. 20): този с най-много статии през 2026 (при
// равенство — най-малкият код). Решение 4: нищо не се създава.
function activityCodeOf(items) {
  const list = (items || []).filter((i) => i && i.code);
  if (!list.length) return null;
  list.sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || String(a.code).localeCompare(String(b.code)));
  return list[0].code;
}
// Мерките по фиксираната таблица, само ако кодът съществува във фирмата
// (при липса на данни за $3 — таблицата, както е).
function measureCodes(items3) {
  const have = items3 ? new Set(items3.map((i) => i.code)) : null;
  const out = {};
  for (const [key, code] of MEASURES) out[key] = !have || have.has(code) ? code : '';
  return out;
}

// Кои номенклатури са „статия" за сметките на категориите (602 → 166…) и
// дали ном. 20 е ниво на разходна сметка. accounts = konto_analytics.accounts.
function expenseLevels(accounts, categoriesAccounts) {
  const out = { itemNom: {}, activity: false, nom20len: 0, project: null, mol: null };
  for (const acc of categoriesAccounts || []) {
    const a = accounts && accounts[acc];
    if (!a || !Array.isArray(a.levels)) continue;
    for (const l of a.levels) {
      if (l.role === 'item') out.itemNom[acc] = { nom: l.nom, len: l.len, nom_name: l.nom_name };
      if (l.role === 'activity') { out.activity = true; out.nom20len = l.len; }
      if (l.role === 'project' && !out.project) out.project = { nom: String(l.nom), len: l.len, nom_name: l.nom_name };
    }
  }
  // „МОЛ/Склад" (ном. 201) е по складовите сметки, не по тези на категориите.
  for (const acc of ['302', '304', '303']) {
    const a = accounts && accounts[acc];
    const l = a && Array.isArray(a.levels) ? a.levels.find((x) => x.role === 'mol') : null;
    if (l && !out.mol) out.mol = { nom: String(l.nom), len: l.len, nom_name: l.nom_name };
  }
  return out;
}

// „Попълни по данните от Конто": вид дейност, мерки и карта по име.
// categories: списъкът (services/kontoCategories); nsb: nsbOf().
function defaultsFor(accounts, nsb, categories) {
  const accs = [...new Set((categories || []).map((c) => c.account))];
  const ex = expenseLevels(accounts, accs);
  const n20 = nsb.get('20');
  const n3 = nsb.get('$3');
  const items = {};
  // Първо сигурните (име / синоним) за всички категории, после ключовите думи
  // — само върху статиите, които никоя категория не е взела.
  const claimed = new Map();
  for (const pass of [1, 2]) {
    for (const c of categories || []) {
      const lv = ex.itemNom[c.account];
      if (!lv) { items[c.id] = null; continue; }
      if (pass === 2 && items[c.id]) continue;
      const nom = String(lv.nom);
      const n = nsb.get(nom);
      const sug = n ? suggest(pass === 1 ? Object.assign({}, c, { keywords: [] }) : c, n.items, claimed.get(nom)) : null;
      items[c.id] = sug;
      if (sug) { if (!claimed.has(nom)) claimed.set(nom, new Set()); claimed.get(nom).add(sug.code); }
    }
  }
  const live = (lv) => (lv && nsb.get(lv.nom) ? nsb.get(lv.nom).items.filter((i) => !i.inactive && i.active !== false) : []);
  return {
    activity: ex.activity, activity_code: ex.activity && n20 ? activityCodeOf(n20.items) : '',
    measures: measureCodes(n3 ? n3.items : null), items, item_noms: ex.itemNom,
    // ПРОМТ-45: проект (ном. 208) и МОЛ/склад (ном. 201) — най-ползваният код.
    project: ex.project, project_code: ex.project ? (activityCodeOf(live(ex.project)) || '') : '',
    mol: ex.mol, mol_code: ex.mol ? (activityCodeOf(live(ex.mol)) || '') : '',
  };
}

module.exports = {
  MEASURES, measureKey, firmKey, roleOf, readWorkbook, accountsByFirm, accountEntry, plan, summary, apply,
  levelsRowOf, withAnalytics, nsbOf, freeCode, normName, suggest, activityCodeOf, measureCodes, expenseLevels, defaultsFor,
  analyticsOf: kontoConfig.analyticsOf,
};
